import React, { useCallback } from 'react';
import { BarDatum } from '@nivo/bar';
import { animated } from '@react-spring/web';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { NumericDisplayUnit } from 'libs/end-to-end/src/metrics';
import { BarCustomLayerProps } from '@nivo/bar/dist/types/types';

export const BarTotalPopoverLayer = (props: BarCustomLayerProps<BarDatum>) => {
  const positiveValuesBaseY = new Set(
    props.bars
      .filter((bar) => (bar.data.value ?? 0) > 0)
      .map((bar) => bar.y + bar.height),
  );

  const negativeValuesBaseY = new Set(
    props.bars.filter((bar) => (bar.data.value ?? 0) < 0).map((bar) => bar.y),
  );

  const shouldDisplayHorizontalBars =
    positiveValuesBaseY.size > 1 || negativeValuesBaseY.size > 1;

  const formatValue = useCallback((value: number, units: string | number) => {
    const parsedUnits = NumericDisplayUnit.safeParse(units);

    if (!parsedUnits.success) {
      return '';
    }

    return formatNumberForDisplayInUnits({
      value,
      units: parsedUnits.data,
      isPercentageAlreadyMultipliedBy100: true,
    });
  }, []);

  return (
    <g>
      {props.bars.map((bar, barIndex) => {
        const barCenterX = bar.x + bar.width / 2;
        const barCenterY = bar.y + bar.height / 2;

        const rectX = shouldDisplayHorizontalBars
          ? bar.width + bar.x + 10
          : barCenterX;

        const rectY = shouldDisplayHorizontalBars ? barCenterY : bar.y - 10;

        const units = bar.data.data[`${bar.data.id}_Units`];

        if (
          !bar.data.value ||
          !isFinite(Number.parseFloat(bar.data.value?.toString() ?? ''))
        ) {
          return null;
        }

        return (
          <animated.g key={barIndex}>
            <text
              x={rectX}
              y={rectY}
              textAnchor={shouldDisplayHorizontalBars ? 'start' : 'middle'}
              dominantBaseline="middle"
              style={{
                fill: 'black',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              {units ? formatValue(bar.data.value, units) : bar.data.value}
            </text>
          </animated.g>
        );
      })}
    </g>
  );
};
